// Dependencies
import React, { useEffect, useRef, useState } from "react";

import {
	IconDefinition,
	faCheckCircle,
	faInfoCircle,
	faTimesCircle,
	faWarning,
} from "@fortawesome/pro-regular-svg-icons";

// Project
import { Toast as IToast } from "./Store";

// Utils
import Timer from "../../utils/timer";

// components
import Icon from "../Icon";
// Styles
import css from "./Toast.module.scss";

const ANIMATION_DURATION = 200;

interface BaseProps extends IToast {
	/**
	 * Callback function when the toast is closed.
	 */
	onClose: () => void;
	/**
	 * Callback function when the toast is finished animating out.
	 */
	onFinish: () => void;
	/**
	 * The index of the toast in the toast container. Used to determine the
	 * position of the toast.
	 */
	positionIndex: number;
	/**
	 * Whether or not the toast is being hovered over.
	 * Used to determine whether or not to pause the timer.
	 */
	hasHover: boolean;
	/**
	 * Icon to display in the toast.
	 */
	icon?: IconDefinition;
	/**
	 *  Size of the toast
	 */
	size?: "small" | "medium" | "large";
}

interface StaticToastProps extends Pick<BaseProps, "message" | "type" | "size" | "icon"> {
	contentStyle?: React.CSSProperties;
	handleClick?: () => void;
	handleHoverState?: (nextState: boolean) => void;
}

const TypeIcon = (props: { type: string }) => {
	switch (props.type) {
		case "error":
			return faTimesCircle;
		case "success":
			return faCheckCircle;
		case "warning":
			return faWarning;
		case "info":
			return faInfoCircle;
		default:
			return null;
	}
};

/**
 * The toast message component without it being bound to the snackbar. This
 * allows us to render it statically on the page. Look at storybook for an
 * example of this.
 * @deprecated Use the Toast component from the py-components package instead
 */
export const StaticToast = (props: StaticToastProps) => {
	const {
		icon,
		message,
		type,
		contentStyle,
		handleClick = () => {},
		handleHoverState = () => {},
		size = "medium",
	} = props;

	const toastIcon = icon || (TypeIcon({ type }) as IconDefinition);

	return (
		<div
			className={`${css.staticToast} ${css[`type-${type}`]} ${css[size]}`}
			onClick={handleClick}
			onMouseEnter={() => handleHoverState(true)}
			onMouseLeave={() => handleHoverState(false)}
			style={contentStyle}
		>
			<Icon className={css.icon} size="regular" icon={toastIcon} />

			<div className={` ${css.toastMessage} `}>{message}</div>
		</div>
	);
};

/**
 * Toast message component that is displayed in the Toast container. Used to
 * display a message to the user.
 * @deprecated Use the Toast component from the py-components package instead
 */
export const Toast = (props: BaseProps) => {
	const { message, type, positionIndex, hasHover, duration = 5000, size = "small", icon } = props;

	const timerRef = useRef<Timer>();
	const [isExiting, setIsExiting] = useState(false);
	const [isEntering, setIsEntering] = useState(true);

	const handleClick = () => {
		//
	};

	/**
	 * Starts the exiting process. It will update the state which will then
	 * change the class to animate the notification box out. After the
	 * animation is done, it will inform the container -- the container will
	 * then remove it from the store.
	 */
	const startExiting = () => {
		setIsExiting(true);
		setTimeout(() => {
			props.onFinish();
		}, ANIMATION_DURATION);
	};

	/**
	 * Starts the entering process. The component initializes the entering
	 * state to true in the 'useState' hook, so we don't need to update the
	 * state here. This will just set a countdown to start the notification
	 * timer and update the entering statewhen the entering animation has
	 * completed.
	 */
	const startEntering = () => {
		setTimeout(() => {
			// setup timer
			const timer = new Timer(duration);
			timer.onFinish = () => {
				startExiting();
			};
			timer.start();

			// set ref
			timerRef.current = timer;

			// update state
			setIsEntering(false);
		}, ANIMATION_DURATION);
	};

	/**
	 * Serves as a replacement to the 'onMount' and 'onUnmount' lifecycle
	 * methods. On mount, it will run the entering process. On unmount, it will
	 * teardown the timer for the case where the user clicks the notification.
	 */
	useEffect(() => {
		// start entering process
		startEntering();

		return () => {
			timerRef.current?.cancel();
		};
	}, []);

	/**
	 * Sets the hover state inside the component. This action will pause the
	 * timer.
	 * @param {bool} nextState - next state value for hover state
	 */
	const handleHoverState = (nextState: boolean) => {
		if (nextState === true) {
			timerRef.current?.pause();
		} else {
			timerRef.current?.resume();
		}
	};

	const toastStyle = hasHover
		? { transform: `translateY(${positionIndex * -46}px)` }
		: { transform: `translateY(${positionIndex * -8}px)`, opacity: Math.pow(0.9, positionIndex) };

	const contentStyle = hasHover ? {} : { transform: `scale(${1 - positionIndex * 0.1})` };

	return (
		<div
			className={`${css.toast} ${isExiting ? css.isExiting : ""} ${isEntering ? css.isEntering : ""}`}
			style={toastStyle}
		>
			<StaticToast
				icon={icon}
				message={message}
				type={type}
				size={size}
				contentStyle={contentStyle}
				handleClick={handleClick}
				handleHoverState={handleHoverState}
			/>
		</div>
	);
};
